/*
 * bz-shipping-bar.js — in-house bar runtime (T-458de7), scope (b): FULL Hextom replacement.
 *
 * Reads window.BZ_SHIPBAR_RULES (generated per market by tools/build_shipping_bar_config.py from
 * config/bz-shipping-bar-rules.json) and renders exactly one bar. This replaces ALL of Hextom's
 * jobs, not just the shipping message — porting only the shipping half would delete cross-market
 * routing and a regulatory notice on uninstall:
 *
 *   redirect  26 rules — cross-market routing (DE/AT/LI -> .de, FR/LU -> .fr, GB -> .com, US -> .us)
 *   info       8 rules — incl. the Ireland PRODUCT-AVAILABILITY restriction
 *   shipping  86 rules — free-shipping threshold messages
 *
 * PRIORITY: rules arrive pre-sorted redirect > info > shipping and the FIRST match wins. Being on
 * the wrong storefront matters more than a shipping threshold; an availability restriction matters
 * more than an offer.
 *
 * FIRST PAINT: the snippet server-renders the market's ungated shipping rule when one exists, so
 * the common case has no layout shift and needs no JS. A country-gated market renders hidden and
 * is revealed only once the country is known — never a wrong-market promise that flashes and dies.
 *
 * CHECKOUT SAFETY IS ABSOLUTE: the cart wrappers sit in the path of every add-to-cart on the store.
 * Nothing here may prevent, delay or reject the caller's request.
 */
(function () {
  'use strict';

  var bar = document.getElementById('bz-shipbar');
  if (!bar) return;

  var CFG = window.BZ_SHIPBAR_RULES;
  if (!CFG || !CFG.rules || !CFG.rules.length) return;   // leave the server-rendered bar alone

  var slotD = bar.querySelector('[data-bz-slot="desktop"]');
  var slotM = bar.querySelector('[data-bz-slot="mobile"]');
  var pWrap = bar.querySelector('[data-bz-progress]');
  var pFill = bar.querySelector('[data-bz-fill]');
  var closeBtn = bar.parentNode && bar.parentNode.querySelector('[data-bz-dismiss]');

  /* BAR HEIGHT PUBLISHER (T-9418d2/T-066c63). The theme's sticky header hardcodes Hextom's 24px.
     Publish our real height so it can read it. Safe here: the IIFE already returned above if
     `bar` was absent, so `bar.parentNode` cannot throw on a page without the bar — which matters
     because this file wraps fetch/XHR on the add-to-cart path. */
  var wrap = bar.parentNode;
  var lastH = null;
  function publishHeight() {
    var h = wrap ? Math.round(wrap.getBoundingClientRect().height) : 0;
    if (h === lastH) return;          /* writing a layout-affecting custom property from inside a
                                         ResizeObserver can re-trigger it; gate on change */
    lastH = h;
    document.documentElement.style.setProperty('--bz-shipbar-h', h + 'px');
  }
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  /* Locale drives money formatting entirely: position, decimal separator and the
   * non-breaking space all come from (locale, currency code) via Intl. Never restate
   * those rules in copy — Austria prefixes the symbol and Switzerland uses a period,
   * and a hand-rolled 'always suffix, always comma' rule gets both of them wrong. */
  var LOCALE = { uk: 'en-GB', us: 'en-US', fr: 'fr-FR', de: 'de-DE' }[(CFG.market || '').toLowerCase()] || 'en-GB';
  var current = null;          // the matched rule
  var cartTotal = Number(bar.getAttribute('data-bz-total')) || 0;

  /* storage throws outright in Safari private mode; unguarded access would abort this IIFE and
   * take cart tracking and analytics with it. */
  var mem = {};
  function sGet(k, persist) {
    try { return (persist ? localStorage : sessionStorage).getItem(k); } catch (e) { return mem[k] || null; }
  }
  function sSet(k, v, persist) {
    try { (persist ? localStorage : sessionStorage).setItem(k, v); } catch (e) { mem[k] = v; }
  }

  function list(v) { return (v || []).slice(); }

  /* ---------- rule matching ---------- */
  function isMobile() { return window.matchMedia('(max-width: 749px)').matches; }

  function ruleMatches(r, country) {
    if (r.d && r.d !== 'all' && r.d !== (isMobile() ? 'mobile' : 'desktop')) return false;

    var path = window.location.pathname;
    if (r.xp && path.indexOf(r.xp) !== -1) return false;
    if (r.dp && r.dp !== 'all' && path.indexOf(r.dp) === -1) return false;

    if (country) {
      if (list(r.xc).indexOf(country) !== -1) return false;
      if (list(r.co).length && list(r.co).indexOf(country) === -1) return false;
    } else if (list(r.co).length) {
      return false;                       // country-gated rule, country unknown -> cannot match
    }
    return true;
  }

  function pickRule(country) {
    /* An unresolved country is NOT the same as "international". resolveCountry() gives up after
       1500ms, which is routine on mobile data — and with country null, ruleMatches() rejects every
       country-gated rule, so the UK store fell through to its ungated "FREE International Shipping
       over GBP 40" rule and told UK customers they had to spend GBP 40 for delivery that is already
       free (T-34881f, reported from an Android phone in the UK). Being on a storefront is itself
       evidence of being in its home market, so assume that rather than guessing wrong. This also
       keeps the runtime's choice consistent with the server-rendered first paint, which is that
       same home-market message. */
    if (!country && CFG.home) country = CFG.home;
    for (var i = 0; i < CFG.rules.length; i++) {
      /* Skip DISMISSED rules here, not just in the dismiss click handler. Without this, the
         fall-through only worked on the page where the visitor clicked the x: every LATER page
         re-picked the dismissed redirect and paint() hid the bar entirely, so declining the
         "switch site" prompt silently cost the visitor their shipping message for 30 days.
         Caught live 2026-08-19 by the persistence leg of the dismiss verification. */
      if (ruleMatches(CFG.rules[i], country) && !isDismissed(CFG.rules[i])) return CFG.rules[i];
    }
    return null;
  }

  /* Dismissal applies to redirect + info only. A shipping message is not dismissible — it is the
   * offer, not an interruption. Persisted so a visitor who has declined the German site once is
   * not asked again on every page. */
  function dismissKey(r) { return 'bz_bar_dismissed:' + (r.id || (r.c + ':' + list(r.co).join(','))); }
  function dismissible(r) { return r && (r.c === 'redirect' || r.c === 'info'); }
  function isDismissed(r) { return dismissible(r) && sGet(dismissKey(r), true) === '1'; }

  /* ---------- money ---------- */
  function money(cents, code) {
    var v = Math.max(0, Number(cents) || 0) / 100;
    var whole = v % 1 === 0;
    try {
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: code || CFG.currency_code || 'GBP',
        // narrowSymbol keeps CAD as "$" for a US audience instead of "CA$"; the Canada bar's
        // own copy already ends in "CAD", and "CA$60 CAD" reads as a mistake. No effect on EUR/GBP.
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(v);
    } catch (e) {
      return (code ? code + ' ' : '') + (whole ? String(v) : v.toFixed(2));
    }
  }

  function fill(tpl, r, total) {
    // goal<=0 short-circuits before any division: UK and US both run goal=0.
    var rem = r.g > 0 ? Math.max(0, r.g - total) : 0;
    var rem2 = r.g2 > 0 ? Math.max(0, r.g2 - total) : 0;
    return String(tpl || '')
      .replace(/\{\{goal_2\}\}/g, money(r.g2, r.cur))
      .replace(/\{\{remainder_2\}\}/g, money(rem2, r.cur))
      .replace(/\{\{goal\}\}/g, money(r.g, r.cur))
      .replace(/\{\{remainder\}\}/g, money(rem, r.cur))
      .replace(/\{\{currency_symbol\}\}/g, '');
  }

  function stateOf(r, total) {
    if (r.c !== 'shipping') return 'static';        // redirect/info have one message
    if (r.g <= 0) return 'free';
    if (!total) return 'one';
    if (total < r.g) return 'two';
    if (r.g2 > 0 && total < r.g2) return 'two2';
    return 'three';
  }

  function messageFor(r, total) {
    var s = stateOf(r, total);
    if (s === 'two') return r.m2 || r.m1;
    if (s === 'three' || s === 'two2') return r.m3 || r.m1;
    return r.m1;
  }

  /* ---------- render ---------- */
  /* Renders a message into a slot. THE TEXT IS ONLY EVER SET VIA textContent — this bar renders on
     every page of every store, so no string from the rule data is ever parsed as HTML. The country
     flag and the underlined call-to-action are therefore built as real elements with createElement
     rather than injected as markup, which is what the old app did and what the port had to drop.
     The flag src is additionally checked against the single allowed CDN prefix emitted by the
     generator, so rule data cannot point the <img> somewhere else. */
  function renderInto(slot, text, flagUrl, cta) {
    if (!slot) return;
    while (slot.firstChild) slot.removeChild(slot.firstChild);
    if (flagUrl && CFG.flagPrefix && flagUrl.indexOf(CFG.flagPrefix) === 0) {
      var img = document.createElement('img');
      img.className = 'bz-shipbar__flag';
      img.src = flagUrl;
      img.alt = '';                                  /* decorative: the country is already in the
                                                        text, so announcing it twice is noise */
      img.setAttribute('aria-hidden', 'true');
      slot.appendChild(img);
      slot.appendChild(document.createTextNode(' '));
    }
    var at = cta ? text.indexOf(cta) : -1;
    if (at === -1) { slot.appendChild(document.createTextNode(text)); return; }
    slot.appendChild(document.createTextNode(text.slice(0, at)));
    var u = document.createElement('u');
    u.appendChild(document.createTextNode(cta));
    slot.appendChild(u);
    slot.appendChild(document.createTextNode(text.slice(at + cta.length)));
  }

  /* ---------- country switch prompt (.de and .fr; 23 October 2026, Francis) ----------
     A redirect rule renders as a question with two equal choices: stay on this store (remembered in
     the geo-redirect snippet's bz_locale cookie, so the prompt does not come back) or go to the
     suggested store. Asked in this store's language; the store names carry the choice.
     Shipping and info rules keep the one-line bar. Needs the GEO PROMPT CSS block in
     snippets/bz-shipping-bar.liquid. */
  /* Vector flags drawn here (official colours and proportions): the 32px PNGs in Shopify Files
     were made for the 11px one-line bar and turn soft at 22px on a phone screen. Shown cropped to
     a circle by CSS (object-fit: cover). */
  var FLAG_GB = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30"><clipPath id="s"><path d="M0,0v30h60V0z"/></clipPath><clipPath id="t"><path d="M30,15h30v15zv15H0zH0V0zV0h30z"/></clipPath><g clip-path="url(#s)"><path d="M0,0v30h60V0z" fill="#012169"/><path d="M0,0L60,30M60,0L0,30" stroke="#fff" stroke-width="6"/><path d="M0,0L60,30M60,0L0,30" clip-path="url(#t)" stroke="#C8102E" stroke-width="4"/><path d="M30,0v30M0,15h60" stroke="#fff" stroke-width="10"/><path d="M30,0v30M0,15h60" stroke="#C8102E" stroke-width="6"/></g></svg>';
  var FLAG_US = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 190 100"><rect width="190" height="100" fill="#fff"/><rect y="0" width="190" height="7.692" fill="#B22234"/><rect y="15.385" width="190" height="7.692" fill="#B22234"/><rect y="30.769" width="190" height="7.692" fill="#B22234"/><rect y="46.154" width="190" height="7.692" fill="#B22234"/><rect y="61.538" width="190" height="7.692" fill="#B22234"/><rect y="76.923" width="190" height="7.692" fill="#B22234"/><rect y="92.308" width="190" height="7.692" fill="#B22234"/><rect width="76" height="53.846" fill="#3C3B6E"/><g fill="#fff"><circle cx="6.33" cy="5.38" r="2"/><circle cx="19" cy="5.38" r="2"/><circle cx="31.67" cy="5.38" r="2"/><circle cx="44.33" cy="5.38" r="2"/><circle cx="57" cy="5.38" r="2"/><circle cx="69.67" cy="5.38" r="2"/><circle cx="12.67" cy="10.77" r="2"/><circle cx="25.33" cy="10.77" r="2"/><circle cx="38" cy="10.77" r="2"/><circle cx="50.67" cy="10.77" r="2"/><circle cx="63.33" cy="10.77" r="2"/><circle cx="6.33" cy="16.16" r="2"/><circle cx="19" cy="16.16" r="2"/><circle cx="31.67" cy="16.16" r="2"/><circle cx="44.33" cy="16.16" r="2"/><circle cx="57" cy="16.16" r="2"/><circle cx="69.67" cy="16.16" r="2"/><circle cx="12.67" cy="21.54" r="2"/><circle cx="25.33" cy="21.54" r="2"/><circle cx="38" cy="21.54" r="2"/><circle cx="50.67" cy="21.54" r="2"/><circle cx="63.33" cy="21.54" r="2"/><circle cx="6.33" cy="26.92" r="2"/><circle cx="19" cy="26.92" r="2"/><circle cx="31.67" cy="26.92" r="2"/><circle cx="44.33" cy="26.92" r="2"/><circle cx="57" cy="26.92" r="2"/><circle cx="69.67" cy="26.92" r="2"/><circle cx="12.67" cy="32.31" r="2"/><circle cx="25.33" cy="32.31" r="2"/><circle cx="38" cy="32.31" r="2"/><circle cx="50.67" cy="32.31" r="2"/><circle cx="63.33" cy="32.31" r="2"/><circle cx="6.33" cy="37.7" r="2"/><circle cx="19" cy="37.7" r="2"/><circle cx="31.67" cy="37.7" r="2"/><circle cx="44.33" cy="37.7" r="2"/><circle cx="57" cy="37.7" r="2"/><circle cx="69.67" cy="37.7" r="2"/><circle cx="12.67" cy="43.08" r="2"/><circle cx="25.33" cy="43.08" r="2"/><circle cx="38" cy="43.08" r="2"/><circle cx="50.67" cy="43.08" r="2"/><circle cx="63.33" cy="43.08" r="2"/><circle cx="6.33" cy="48.46" r="2"/><circle cx="19" cy="48.46" r="2"/><circle cx="31.67" cy="48.46" r="2"/><circle cx="44.33" cy="48.46" r="2"/><circle cx="57" cy="48.46" r="2"/><circle cx="69.67" cy="48.46" r="2"/></g></svg>';
  var FLAG_DE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 3"><path d="M0,0h5v3H0z"/><path d="M0,1h5v2H0z" fill="#D00"/><path d="M0,2h5v1H0z" fill="#FFCE00"/></svg>';
  var FLAG_FR = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2"><path d="M0,0h1v2H0z" fill="#002654"/><path d="M1,0h1v2H1z" fill="#fff"/><path d="M2,0h1v2H2z" fill="#CE1126"/></svg>';
  var STORE_INFO = {
    GB: { name: 'United Kingdom', short: 'UK', flag: FLAG_GB, lang: 'en' },
    US: { name: 'United States', short: 'US', flag: FLAG_US, lang: 'en' },
    DE: { name: 'Deutschland', short: 'DE', flag: FLAG_DE, lang: 'de' },
    FR: { name: 'France', short: 'FR', flag: FLAG_FR, lang: 'fr' }
  };
  var STORE_OF_HOST = {
    'brainzyme.com': 'GB', 'brainzyme.us': 'US', 'brainzyme.de': 'DE', 'brainzyme.fr': 'FR'
  };
  var ASK = {
    en: { q: 'Are you in the right place?', or: 'or' },
    de: { q: 'Sind Sie hier richtig?', or: 'oder' },
    fr: { q: 'Êtes-vous au bon endroit ?', or: 'ou' }
  };
  var geo = null, geoQ, geoOr, geoStay, geoGo;

  function storeOf(url) {
    try { return STORE_OF_HOST[new URL(url, location.href).host.replace(/^www\./, '')] || null; } catch (e) { return null; }
  }
  function option(tag, cls, info) {
    var el = document.createElement(tag);
    el.className = 'bz-geobar__opt ' + cls;
    var img = document.createElement('img');
    img.className = 'bz-geobar__flag'; img.alt = ''; img.setAttribute('aria-hidden', 'true');
    img.src = 'data:image/svg+xml,' + encodeURIComponent(info.flag);
    var n = document.createElement('span'); n.className = 'bz-geobar__n'; n.textContent = info.name;
    var s = document.createElement('span'); s.className = 'bz-geobar__s'; s.textContent = info.short;
    el.appendChild(img); el.appendChild(n); el.appendChild(s);
    return el;
  }
  function renderGeo(r, href) {
    var here = STORE_INFO[CFG.home], code = storeOf(href), there = code && STORE_INFO[code];
    if (!here || !there || code === CFG.home) return false;   // unknown target: keep the one-line bar
    var t = ASK[here.lang] || ASK.en;
    if (geo) geo.parentNode.removeChild(geo);
    geo = document.createElement('div');
    geo.className = 'bz-geobar';
    geo.setAttribute('role', 'region');
    geo.setAttribute('aria-label', t.q);
    geoQ = document.createElement('p'); geoQ.className = 'bz-geobar__q'; geoQ.textContent = t.q;
    var opts = document.createElement('div'); opts.className = 'bz-geobar__opts';
    geoStay = option('button', 'bz-geobar__opt--stay', here); geoStay.type = 'button';
    geoOr = document.createElement('span'); geoOr.className = 'bz-geobar__or'; geoOr.textContent = t.or;
    geoGo = option('a', 'bz-geobar__opt--go', there); geoGo.href = href;
    opts.appendChild(geoStay); opts.appendChild(geoOr); opts.appendChild(geoGo);
    geo.appendChild(geoQ); geo.appendChild(opts);
    wrap.insertBefore(geo, bar);

    geoGo.addEventListener('click', function () { if (current) push('bz_shipping_bar_click', current); });
    geoStay.addEventListener('click', function () {
      if (!current) return;
      /* same cookie, format and lifetime as the geo snippet's setLocale(): this store is the choice */
      document.cookie = 'bz_locale=' + CFG.home + '; max-age=31536000; path=/; secure; samesite=lax';
      push('bz_shipping_bar_stay', current);
      dismissCurrent();
    });
    return true;
  }

  function paint(r, total) {
    if (!r || isDismissed(r)) { bar.setAttribute('hidden', 'hidden'); if (geo) geo.hidden = true; if (wrap) wrap.classList.remove('bz-geo'); hideClose(); return null; }

    var msg = fill(messageFor(r, total), r, total);
    /* ONE flag mechanism — the image — on BOTH slots (Calum 2026-08-19). The slots are chosen by
       viewport width, not device, so "emoji on the mobile slot" really meant "emoji in any window
       under 750px", including Windows desktop windows where a regional-indicator pair renders as a
       small "GB" letterbox instead of a flag. The generator strips the emoji from the message text
       in the same change, so a slot never shows two flags. */
    renderInto(slotD, msg, r.fl, r.u);
    renderInto(slotM, msg, r.fl, r.u);
    if (r.ln) {
      var href = r.ln;
      /* Cross-domain attribution: each store is its own GA4 property, so a redirect click ends one
         property's session and starts another's. UTM-stamping the destination makes the arrival
         deterministic on the destination property (session_source=brainzyme_<mkt> /
         medium=shipping_bar) instead of a guess from referrer. Only redirect rules leave the
         domain; shipping/info links stay on-store and must NOT be stamped. */
      if (r.c === 'redirect' && href.indexOf('utm_') === -1) {
        href += (href.indexOf('?') === -1 ? '?' : '&') +
                'utm_source=brainzyme_' + CFG.market + '&utm_medium=shipping_bar' +
                '&utm_campaign=cross_market_redirect' +
                '&utm_content=' + encodeURIComponent((sGet('bz_country') || 'xx') + ':' + (r.id || 'new'));
      }
      bar.setAttribute('href', href);
    }
    var geoOn = r.c === 'redirect' && !!r.ln && renderGeo(r, bar.getAttribute('href'));

    bar.setAttribute('data-bz-class', r.c);
    bar.setAttribute('data-bz-state', stateOf(r, total));
    bar.setAttribute('data-bz-total', String(total));
    if (r.id) bar.setAttribute('data-bz-rule', String(r.id));
    if (geoOn) { bar.setAttribute('hidden', 'hidden'); geo.hidden = false; }
    else { bar.removeAttribute('hidden'); if (geo) geo.hidden = true; }
    if (wrap) wrap.classList.toggle('bz-geo', geoOn);

    if (CFG.progress && pWrap && pFill && r.c === 'shipping' && r.g > 0) {
      var pct = Math.min(100, Math.round((total / r.g) * 100));
      pFill.style.width = pct + '%';
      pWrap.setAttribute('aria-valuenow', String(pct));
      pWrap.style.display = 'block';
    } else if (pWrap) {
      pWrap.style.display = 'none';
    }

    if (closeBtn) closeBtn.hidden = !dismissible(r);
    /* Padding is reserved for the dismiss button only when it is actually shown, so the text stays
       centred on the shipping rules that make up most impressions. */
    if (wrap) wrap.classList.toggle('bz-has-x', dismissible(r));
    current = r;
    publishHeight();                  /* the message just changed: it may have re-wrapped */
    return r;
  }

  function hideClose() {
    if (closeBtn) closeBtn.hidden = true;
    if (wrap) wrap.classList.remove('bz-has-x');
    publishHeight();
  }

  function dismissCurrent() {
    sSet(dismissKey(current), '1', true);
    // fall through to the next matching rule rather than leaving a gap
    var country = sGet('bz_country');
    var next = null;
    for (var i = 0; i < CFG.rules.length; i++) {
      var r = CFG.rules[i];
      if (r !== current && ruleMatches(r, country) && !isDismissed(r)) { next = r; break; }
    }
    paint(next, cartTotal);
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', function (ev) {
      ev.preventDefault(); ev.stopPropagation();
      if (!current) return;
      push('bz_shipping_bar_dismiss', current);
      dismissCurrent();
    });
  }

  /* ---------- analytics ---------- */
  function push(event, r) {
    var params = {
      bz_market: CFG.market,
      bz_class: r && r.c,
      bz_rule: r && r.id,
      bz_state: r ? stateOf(r, cartTotal) : null,
      bz_goal: r && r.g,
      bz_remainder: r && r.g > 0 ? Math.max(0, r.g - cartTotal) : 0,
      bz_device: isMobile() ? 'mobile' : 'desktop',
      bz_country: sGet('bz_country')
    };
    /* The stores run the plain Google tag (gtag.js), NOT GTM — and gtag does not forward raw
       dataLayer.push OBJECTS to GA4, so these events emitted into the void until 2026-08-19.
       Two subtleties, both learned the hard way on the same day:
       1. gtag('event', ...) is the call that reaches GA4 — but this bar boots BEFORE gtag.js
          loads, so a `typeof window.gtag` guard silently DROPS the impression event. Google's own
          bootstrap pattern is the fix: define the same shim the official snippet defines (push the
          arguments object into dataLayer); gtag.js replays queued arguments-objects when it loads.
          Works identically before and after load — no race.
       2. transport_type 'beacon', because a redirect click NAVIGATES AWAY — a normal request can
          be cancelled by the unload, a beacon survives it. */
    params.transport_type = 'beacon';
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      window.gtag('event', event, params);
    } catch (e) {}
  }
  var impressed = false;
  function impressionOnce() {
    if (impressed || !current || (bar.hasAttribute('hidden') && !(geo && !geo.hidden))) return;
    impressed = true;
    push('bz_shipping_bar_impression', current);
  }
  bar.addEventListener('click', function () { if (current) push('bz_shipping_bar_click', current); });

  /* ---------- country ---------- */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function resolveCountry(done) {
    var cached = sGet('bz_country');
    if (cached) { done(cached); return; }
    if (!nativeFetch || !window.AbortController) { done(null); return; }
    var ctl = new AbortController();
    var timer = setTimeout(function () { ctl.abort(); }, 1500);
    nativeFetch(root() + 'browsing_context_suggestions.json?country[enabled]=true', { signal: ctl.signal })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var c = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        if (c) sSet('bz_country', c);
        done(c || null);
      })
      .catch(function () { done(null); })
      .then(function () { clearTimeout(timer); });
  }

  /* ---------- cart ---------- */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function refresh() {
    if (!nativeFetch) return;
    nativeFetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (c && typeof c.total_price === 'number') {
          cartTotal = c.total_price;
          if (current) paint(current, cartTotal);
        }
      })
      .catch(function () { /* keep the last known good state */ });
  }

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var p = nativeFetch(input, init);            // native call first, returned unconditionally
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CART_WRITE.test(url) && p && p.then) {
          p.then(function () { try { refresh(); } catch (e) {} }, function () {});
        }
      } catch (e) { /* our bookkeeping failed; the caller's request is untouched */ }
      return p;
    };
  }
  var openXHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (openXHR) {
    window.XMLHttpRequest.prototype.open = function () {
      try {
        if (CART_WRITE.test(String(arguments[1]))) {
          this.addEventListener('load', function () { try { refresh(); } catch (e) {} });
        }
      } catch (e) {}
      return openXHR.apply(this, arguments);
    };
  }
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (e) {
    document.addEventListener(e, refresh);
  });

  // exposed for the test harness; harmless in production
  bar.bzPick = pickRule; bar.bzPaint = paint; bar.bzRules = CFG.rules;

  /* Re-measure on any box change: rotate, font swap, message change, dismiss. The resize listener
     is the fallback for browsers without ResizeObserver; the synchronous call covers first paint. */
  if (window.ResizeObserver && wrap) { new ResizeObserver(publishHeight).observe(wrap); }
  else { window.addEventListener('resize', publishHeight); }
  publishHeight();

  resolveCountry(function (country) {
    paint(pickRule(country), cartTotal);
    impressionOnce();
  });
})();
